import axios from "axios";
import { CustomFieldValidator } from "../components/common/Form";
import { ucEndpointsUrls } from "../config/config";
import { User } from "../types/users";
import { doesNotExist, getErrorMessageFromRequest } from "./utils";

const MINIMUM_PASSWORD_LENGTH = process.env.MINIMUM_PASSWORD_LENGTH || 12;

export const composeValidators = (...validators: CustomFieldValidator[]) => (value: string) =>
  validators.reduce((error, validator) => error || validator(value), undefined);
export const required = (required?: boolean) => (value: any) =>
  required && (!value || value === "") ? "This field is required" : undefined;
export const validateEmailString = (value: string) =>
  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? "Invalid email address" : undefined;
/**
 * Validate email field.
 *
 * @param {String} email        - Email address.
 * @return {String|Undefined}   - If errors returns string, otherwise undefined.
 */
export const validateUsedEmail = async (email: string) => {
  try {
    const used = (await axios.post(ucEndpointsUrls.checkUsed, { key: "email", value: email })).data;
    return used ? "An UrbanSim user account with that email already exists." : undefined;
  } catch (error) {
    return getErrorMessageFromRequest(error);
  }
};
export const validatePassword = (password: string) => {
  if (!password) {
    return "This field is required";
  }
  const errorMessage =
    // eslint-disable-next-line max-len
    `Password must be a minimum of ${MINIMUM_PASSWORD_LENGTH} characters and contain at least one upper, lower, and numeric character.`;
  const hasUppercase = (string: string) => /[A-Z]/.test(string);
  const hasLowercase = (string: string) => /[a-z]/.test(string);
  const hasNumber = (string: string) => /[0-9]/.test(string);
  if (
    password.length < MINIMUM_PASSWORD_LENGTH ||
    !hasNumber(password) ||
    !hasLowercase(password) ||
    !hasUppercase(password)
  ) {
    return errorMessage;
  }
  return;
};
/**
 * Validate username field.
 *
 * @param {String} username - Username.
 * @return {String|Undefined}   - If errors returns string, otherwise undefined.
 */
export const validateUsedUser = async (username: string) => {
  try {
    const used = (await axios.post(ucEndpointsUrls.checkUsed, { key: "user", value: username }))
      .data;
    return used ? "An UrbanSim user account with that username already exists." : undefined;
  } catch (error) {
    return getErrorMessageFromRequest(error);
  }
};

/**
 * Validate email already in organization.
 *
 * @param {String} email - email.
 * @return {String|Undefined}   - If errors returns string, otherwise undefined.
 */
export const validateUsedInOrganization = (existingUsers: User[]) => (email: string) => {
  const existingUser: any = existingUsers.find(u => u.email === email);
  if (doesNotExist(existingUser)) return undefined;
  if (!existingUser.accepted || existingUser.expired) {
    return (
      "The user associated with that email has either not yet accepted your previous invitation " +
      "or the invitation has expired. Please reinvite this user instead."
    );
  }
  return "An UrbanSim user account with that email already belongs to your account.";
};
